import { useLazyQuery, useMutation } from "@apollo/client";
import { logEvent } from "firebase/analytics";
import { signInWithEmailAndPassword } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { analytics, auth } from "../../Config/firebase";
import {
  ENABLE_BUSINESS_PORTAL,
  MY_ACCOUNT,
  TELEMETRY_EVENT_LOGIN,
  TELEMETRY_FAILURE,
  TELEMETRY_PLATFORM,
  TELEMETRY_SUCCESS,
  USER_NOT_FOUND,
} from "../../Constants/constants";
import { ISAM, ISAM_UNIFIED_LOGIN, PAYMENT_DELAY_WARNING } from "../../Pages/Login/query";
import { GET_PUBLIC_KEY } from "../../Pages/Signin/query";
import { setForceReset, setUserLoggedInSuccess, userInfo } from "../../Redux/Slices/UserSlice";
import { ACTION_TELEMETRY } from "../../Routes/query";
import {
  RSAOAEPPadding,
  splitMessageByBracesAndFullStop,
  validInputSubmit,
} from "../../Services/helper";

interface UseLoginFormOptions {
  setUser: (user: any) => void;
  // Resolved against the current page URL, so pages nested deeper than /login
  // must pass an absolute path to land on the same portal.
  enterprisePortalUrl?: string;
}

// The sign-in flow of /login (Pages/Login/Login.tsx), for the FiberFirst login screen, so a
// FiberFirst customer is signed in exactly as on /login. /login keeps its own copy of this code
// on this branch: a change to one must be made to the other.
export const useLoginForm = ({
  setUser,
  enterprisePortalUrl = "enterpriseportal/",
}: UseLoginFormOptions) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [lsamError, setLsamError] = useState<boolean>(false);
  const [lsamErrorMessage, setLsamErrorMessage] = useState<string[]>([]);
  const [openDetailsRequiredPopup, setOpenDetailsRequiredPopup] = useState<boolean>(false);
  const [PaymentWarningMessage, setPaymentWarningMessage] = React.useState<any>({
    open: false,
    message: "",
    vertical: "top",
    horizontal: "center",
  });

  const [getPublicKey, { data: publicKey }] = useLazyQuery(GET_PUBLIC_KEY);
  const [actionTelemetry] = useMutation(ACTION_TELEMETRY);

  // this is for commercial portal feedback after successful business linking
  const [accountLinkedSuccessMessage, setAccountLinkedSuccessMessage] = useState("");
  const { openErrorModal } = useSelector((state: any) => state.appProposal);

  const handleEmailInput = (data: any) => {
    setEmail(data);
    setErrorMsg("");
    setEmailError("");
  };

  const handlePasswordInput = (e: any) => {
    setPassword(e.target.value);
    setPasswordError("");
    setErrorMsg("");
  };

  const checkBoxHandler = () => {
    setChecked(!checked);
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      let userCredentials: any = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredentials?.user;
      dispatch(setUserLoggedInSuccess(true));
      const tokenResponse = userCredentials?._tokenResponse;
      localStorage.setItem("tokenResponse", JSON.stringify(tokenResponse));
      setUser(user);
      window.clarity("set", "userEmail", email);
      navigate("/checking-account");
      logEvent(analytics, "Web_Login_Success");
      handleActionTelemetry(TELEMETRY_SUCCESS);
      setLoading(false);
      window?.minimizeChat && window?.minimizeChat();
      const robotsMetaTag = document.querySelector('meta[name="robots"]');
      if (robotsMetaTag) {
        robotsMetaTag.remove();
      }

      // Remove canonical link
      const canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) {
        canonicalLink.remove();
      }
      if (checked) {
        localStorage.setItem("lastLogin", new Date().toDateString());
      }
    } catch (error: any) {
      setLoading(false);
      logEvent(analytics, "Web_Login_Failed");
      handleActionTelemetry(TELEMETRY_FAILURE, error);
      setErrorMsg(USER_NOT_FOUND);
    }
    setLoading(false);
  };

  // Unified login flow for enterprise portal
  const handleUnifiedLogin = async () => {
    setLoading(true);
    try {
      const userCredentials: any = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredentials?.user;
      const tokenResponse = userCredentials?._tokenResponse;

      // Store token
      localStorage.setItem("tokenResponse", JSON.stringify(tokenResponse));
      setUser(user);
      dispatch(setUserLoggedInSuccess(true));

      // Read user types once
      const isBusiness = localStorage.getItem("isBusiness") === "true";
      const isResidential = localStorage.getItem("isResidential") === "true";

      // Redirects
      if (isBusiness) {
        window.location.replace(enterprisePortalUrl);
        return;
      }

      // if no account type is set, redirect to no account found
      if (!isBusiness && !isResidential) {
        navigate("/acc-not-found");
        return;
      }

      // Navigate to home
      navigate("/home");

      // Telemetry
      logEvent(analytics, "Web_Login_Success");
      handleActionTelemetry(TELEMETRY_SUCCESS);

      // UI adjustments
      window?.minimizeChat?.();
      document.querySelector('meta[name="robots"]')?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();

      // Remember login
      if (checked) {
        localStorage.setItem("lastLogin", new Date().toDateString());
      }
    } catch (error: any) {
      logEvent(analytics, "Web_Login_Failed");
      handleActionTelemetry(TELEMETRY_FAILURE, error);
      setErrorMsg(USER_NOT_FOUND);
    } finally {
      setLoading(false);
    }
  };

  const handleActionTelemetry = async (status: any, message?: any) => {
    const telemetryObj = {
      event_name: TELEMETRY_EVENT_LOGIN,
      platform: TELEMETRY_PLATFORM,
      customer_email: email,
      event_final_status: status,
      meta_data: message,
    };
    try {
      const encryptedData = RSAOAEPPadding(
        JSON.stringify(telemetryObj),
        publicKey?.getPublicKey?.publicKey
      );
      await actionTelemetry({
        variables: {
          payload: encryptedData,
        },
      });
    } catch (err) {
      console.log(err);
    }
  };

  const checkValidation = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!checked) {
      localStorage.setItem("KEEP_ME_LOGGED_IN", "false");
    }
    const errors = validInputSubmit({ email, password });
    if (Object.keys(errors).length > 0) {
      setEmailError(errors.email);
      setPasswordError(errors.password);
    }
    const isNotEmpty = Object.values(errors).every((value: any) => value === "");
    if (isNotEmpty) {
      try {
        ENABLE_BUSINESS_PORTAL ? handleUnifiedLsam() : handleIsam();
      } catch (e) {
        console.log(e);
      }
    }
  };

  const [getPaymentWarningMessage, { data: PaymentWarningMessageData }] = useLazyQuery(
    PAYMENT_DELAY_WARNING,
    {
      onCompleted: (data) => {
        setPaymentWarningMessage({
          ...PaymentWarningMessage,
          open: data.homeScreenMessage?.error,
          message: data.homeScreenMessage?.message,
          autopayMessage: data.homeScreenMessage?.newAccountsMessage,
        });
      },
    }
  );

  useEffect(() => {
    getPaymentWarningMessage();
    getPublicKey();
  }, []);

  const [lsam, { loading: userLoading }] = useMutation(ISAM, {
    onError: (error: any) => {
      if (error.networkError.result?.errors[0]?.code === "503") {
        setLsamError(true);
      } else if (error.networkError.result?.errors[0]?.code === "403") {
        setOpenPopup(true);
        dispatch(setForceReset(true));
        dispatch(userInfo({ email: email }));
      } else if (error.networkError.result?.errors[0]?.code === "409") {
        const errorMessage = error.networkError.result?.errors[0]?.message;
        const splitMessage = splitMessageByBracesAndFullStop(errorMessage);
        setLsamErrorMessage(splitMessage);
        setOpenDetailsRequiredPopup(true);
      } else {
        handleLogin();
      }
    },
  });

  // Unified login flow for enterprise portal
  const [lsamUnifiedLogin, { loading: lsamLoading }] = useMutation(ISAM_UNIFIED_LOGIN, {
    onError: (error: any) => {
      if (error.networkError.result?.errors[0]?.code === "503") {
        setLsamError(true);
      } else if (error.networkError.result?.errors[0]?.code === "403") {
        setOpenPopup(true);
        dispatch(setForceReset(true));
        dispatch(userInfo({ email: email }));
      } else if (error.networkError.result?.errors[0]?.code === "409") {
        const errorMessage = error.networkError.result?.errors[0]?.message;
        const splitMessage = splitMessageByBracesAndFullStop(errorMessage);
        setLsamErrorMessage(splitMessage);
        setOpenDetailsRequiredPopup(true);
      } else {
        handleUnifiedLogin();
      }
    },
  });

  const handleUnifiedLsam = async () => {
    try {
      const response = await lsamUnifiedLogin({
        variables: {
          email: email,
          password: password,
          // app: MY_ACCOUNT,
        },
      });
      if (response?.data?.lsam?.error) {
        handleUnifiedLogin();
      } else if (response?.data) {
        const enableBusinessPortal = response?.data?.lsam?.enableBusinessPortal;
        localStorage.setItem("isBusiness", response?.data?.lsam?.business);
        localStorage.setItem("isResidential", response?.data?.lsam?.residential);
        localStorage.setItem("enableBusinessPortal", response?.data?.lsam?.enableBusinessPortal);
        if (enableBusinessPortal) {
          handleUnifiedLogin();
        } else {
          handleLogin();
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleIsam = async () => {
    try {
      const response = await lsam({
        variables: {
          email: email,
          password: password,
          app: MY_ACCOUNT,
        },
      });
      if (response?.data?.lsam?.error) {
        handleLogin();
      } else if (response?.data) {
        handleLogin();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const loader = userLoading || lsamLoading || loading;

  const onEnterSubmit = (e: any) => {
    if (e.keyCode === 13) {
      checkValidation(e);
    }
  };

  const handleRegisterRedirect = () => {
    navigate({ pathname: "/register" }, { state: { data: email ? email : "" } });
  };

  // Forgot password sends the user back to whichever login screen they came from.
  const handleForgotPasswordRedirect = () => {
    navigate("/login/forgot-password", { state: { from: pathname } });
  };

  useEffect(() => {
    const message = sessionStorage.getItem("loginSnackbarMessage");
    if (message) {
      setAccountLinkedSuccessMessage(message);
      sessionStorage.removeItem("loginSnackbarMessage");
    }
  }, []);

  return {
    email,
    password,
    emailError,
    passwordError,
    errorMsg,
    checked,
    loader,
    PaymentWarningMessage,
    PaymentWarningMessageData,
    accountLinkedSuccessMessage,
    setAccountLinkedSuccessMessage,
    openErrorModal,
    lsamError,
    setLsamError,
    openPopup,
    openDetailsRequiredPopup,
    lsamErrorMessage,
    handleEmailInput,
    handlePasswordInput,
    checkBoxHandler,
    checkValidation,
    onEnterSubmit,
    handleRegisterRedirect,
    handleForgotPasswordRedirect,
  };
};

export type LoginFormState = ReturnType<typeof useLoginForm>;
